import { useCallback, useEffect, useRef, useState } from "react";

import { useToast } from "providers/ToastProvider";

import { getSocketByARCInfo, getServiceCallerByARCInfo } from "lib/utils/connection";
import { ConnectionState } from "lib/consts/ARC/connection";

const closeControlSocket = (controlSocket) => {
    if (!controlSocket) {
        return;
    }
    try {
        if (typeof controlSocket.close === "function") {
            controlSocket.close();
        } else if (controlSocket.socket?.close) {
            controlSocket.socket.close();
        }
    } catch (error) {
        console.warn("[useARCConnection] Error closing ARC socket:", error);
    }
};

/**
 * Hook to manage ARC socket connection
 * Handles connection state, socket lifecycle, and event callbacks
 */
const useConnectARC = ({ selectedARC, onConnect, onDisconnect, onError }) => {
    const [connectionState, setConnectionState] = useState(ConnectionState.Disconnected);
    const [controlSocket, setControlSocket] = useState(null);
    const [serviceCaller, setServiceCaller] = useState(null);
    /** Tracks the live socket instance even before React state is updated on connect. */
    const activeControlSocketRef = useRef(null);

    const disconnectARC = useCallback(() => {
        const socket = activeControlSocketRef.current;
        // Clear before close so stale `onDisconnect` handlers become no-ops.
        activeControlSocketRef.current = null;
        closeControlSocket(socket);
        setControlSocket(null);
        setServiceCaller(null);
        setConnectionState(ConnectionState.Disconnected);
    }, []);

    const connectToARC = useCallback(() => {
        if (!selectedARC) return;

        const priorSocket = activeControlSocketRef.current;
        activeControlSocketRef.current = null;
        closeControlSocket(priorSocket);

        setConnectionState(ConnectionState.Connecting);
        const nextControlSocket = getSocketByARCInfo(selectedARC);
        activeControlSocketRef.current = nextControlSocket;

        nextControlSocket.onConnect((...args) => {
            if (activeControlSocketRef.current !== nextControlSocket) {
                return;
            }
            setConnectionState(ConnectionState.Connected);
            setControlSocket(nextControlSocket);

            // Initialize service caller after socket is connected
            const caller = getServiceCallerByARCInfo(selectedARC, nextControlSocket);
            setServiceCaller(caller);

            onConnect(...args);
        });
        nextControlSocket.onDisconnect((...args) => {
            if (activeControlSocketRef.current !== nextControlSocket) {
                return;
            }
            activeControlSocketRef.current = null;
            setControlSocket(null);
            setServiceCaller(null);
            setConnectionState(ConnectionState.Disconnected);
            onDisconnect(...args);
        });

        nextControlSocket.onError((...args) => {
            if (activeControlSocketRef.current !== nextControlSocket) {
                return;
            }
            setConnectionState(ConnectionState.Error);
            onError(...args);
        });
    }, [selectedARC, onConnect, onError, onDisconnect]);

    return { controlSocket, serviceCaller, connectionState, connectToARC, disconnectARC };
};

/**
 * Hook to create ARC socket event handlers with toast notifications
 */
const useARCSocketEventHandlers = ({ selectedARC }) => {
    const { addToast } = useToast();

    const onConnect = useCallback(() => {
        console.log(`Connected to ${selectedARC.serialNumber}`);

        addToast({
            type: "success",
            message: `Successfully connected to ${selectedARC.serialNumber}!`,
        });
    }, [selectedARC, addToast]);

    const onDisconnect = useCallback(() => {
        console.error(`Disconnected from ${selectedARC.serialNumber}`);

        addToast({
            type: "error",
            message: `Disconnected from ${selectedARC.serialNumber}!`,
        });
    }, [addToast, selectedARC]);

    const onError = useCallback(
        (error) => {
            console.error("Error when connecting to web socket", error);

            addToast({
                type: "error",
                message: "Could not connect ARC! (Network error)",
            });
        },
        [addToast]
    );

    return { onConnect, onDisconnect, onError };
};

/**
 * Shared hook for ARC connection logic
 * Used by both ARCProvider (fleet mode) and SingleARCProvider (arc mode)
 *
 * @param {Object} selectedARC - ARC info object with host, serialNumber, etc.
 * @param {Object} options
 * @param {boolean} options.silent - When true, suppresses toast notifications on connect/disconnect/error.
 * @returns {Object} Connection context value with controlSocket, connectionState, sendCommand
 */
export const useARCConnection = (selectedARC, { silent = false } = {}) => {
    const toastHandlers = useARCSocketEventHandlers({ selectedARC });
    const noop = useCallback(() => {}, []);
    const { onConnect, onDisconnect, onError } = silent
        ? { onConnect: noop, onDisconnect: noop, onError: noop }
        : toastHandlers;

    const {
        controlSocket,
        serviceCaller,
        connectionState,
        connectToARC: connectToSocket,
        disconnectARC: disconnectSocket,
    } = useConnectARC({
        selectedARC,
        onConnect,
        onDisconnect,
        onError,
    });

    const sendCommand = useCallback(
        (args) => {
            if (!controlSocket) return;

            controlSocket.send(...args);
        },
        [controlSocket]
    );

    const callService = useCallback(
        async (serviceName, data, options) => {
            if (!serviceCaller) {
                throw new Error("Service caller not initialized");
            }

            return serviceCaller.callService(serviceName, data, options);
        },
        [serviceCaller]
    );

    useEffect(() => {
        if (!selectedARC) return;

        connectToSocket();

        const handlePageUnload = () => {
            disconnectSocket();
        };
        window.addEventListener("pagehide", handlePageUnload);

        return () => {
            window.removeEventListener("pagehide", handlePageUnload);
            disconnectSocket();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedARC]);

    return {
        controlSocket,
        connectionState,
        sendCommand,
        callService,
        selectedARC,
    };
};
